import type { PageSelector } from "./builderTypes";
import { productDelay } from "@/utils/productTimers";
import {
  ComponentRequestError,
  didNotAnswer,
  failedToLoad,
  MAX_BATCH_PLACEMENTS,
  RenderBusyError,
  streamRenderBatch,
  type BatchLine,
  type ComponentNoun,
  type ComponentSummary,
  type RenderBatchOptions,
  type RenderPushSource,
} from "./libraryApi";
import type { ComponentDocument, TableRequest } from "./types";

/** What a render needs of a placed component: which one, at which revision. */
export type PlacedComponent = Pick<
  ComponentSummary,
  "id" | "current_revision_id"
>;

/** Renders one placed component; the same shape a frame's `load` already calls. */
export type RenderPlacement = (
  component: PlacedComponent,
  context: PageSelector,
  tables: Readonly<Record<string, TableRequest>>,
  signal: AbortSignal
) => Promise<ComponentDocument>;

/** A card's answer, with when the server rendered it if it came from its render cache. */
export interface KeptAnswer {
  document: ComponentDocument;
  /** Epoch ms of a cached answer's render; null for a render made for this request. */
  cachedAt: number | null;
  /** The kinds the render read, when the server named them (#13380). */
  kindsRead?: string[] | null;
  /** Whether this render first recorded its revision's kind (#13380). */
  kindRecorded?: boolean;
  /**
   * A server cache entry past its fresh window answered (dashboard polish, requirement 15): the
   * card shows it and renders again past the cache.
   */
  stale?: boolean;
}

/** How one ask may use the server's render cache: `fresh` renders past it (Refresh). */
export interface RenderAsk {
  fresh?: boolean;
}

/** Renders one placed component and says when the server rendered it. */
export type RenderKept = (
  component: PlacedComponent,
  context: PageSelector,
  tables: Readonly<Record<string, TableRequest>>,
  signal: AbortSignal,
  ask?: RenderAsk
) => Promise<KeptAnswer>;

/** A page's batcher: a frame's render, and the same render with its `cached_at`. */
export type PageRenderBatcher = RenderPlacement & { kept: RenderKept };

/**
 * A card the server refused: its render line answered `error`. The message is the server's
 * product copy (#12951), so the card shows it; a transport failure is a plain `Error`.
 */
export class RenderLineError extends Error {
  /** The line's code (`forbidden`, `not_found`, `invalid`, ...), when the server sent one. */
  readonly code: string | null;
  constructor(message: string, code: string | null = null) {
    super(message);
    this.name = "RenderLineError";
    this.code = code;
  }
}

/** The line codes that deny the card to its viewer, as opposed to a render that failed. */
const REFUSAL_CODES: ReadonlySet<string> = new Set(["forbidden", "not_found"]);

/**
 * Whether the server refused the card (#15213): a denial, which the card always shows, never a
 * render that failed, behind which a card keeps the answer it holds. A refusal is a render line
 * that denies the card, or a request refused as a whole: the batch (401 or 403 on its POST) or
 * the widget's summary (403, or 404 for a component gone). A lost job (404 on the batch's read)
 * is a failed render.
 */
export const isRefusal = (error: unknown): boolean =>
  (error instanceof RenderLineError &&
    error.code !== null &&
    REFUSAL_CODES.has(error.code)) ||
  (error instanceof ComponentRequestError &&
    (error.status === 401 || error.status === 403 || error.status === 404));

/**
 * What a failed card says under its generic text: the server's refusal, when the failure is one
 * and it carries a message. A transport failure and an empty message (the line the client
 * makes for a document it could not read) say nothing.
 */
export const refusalReason = (error: unknown): string | null =>
  error instanceof RenderLineError && error.message ? error.message : null;

/** One frame waiting for a card's document. */
interface Waiter {
  signal: AbortSignal;
  resolve: (answer: KeptAnswer) => void;
  reject: (error: Error) => void;
  settled: boolean;
}

/** One card to render, shared by every frame that asks for it while it is waiting or in flight. */
interface Placement {
  key: string;
  component: PlacedComponent;
  tables: Readonly<Record<string, TableRequest>>;
  waiters: Waiter[];
  /** Set once the placement's request has left; a late asker joins it through this. */
  join: ((waiter: Waiter) => void) | null;
  done: boolean;
}

interface Group {
  context: PageSelector;
  /** Sent past the server's render cache: a request of its own. */
  fresh: boolean;
  placements: Placement[];
}

const abortError = () =>
  new DOMException("Component render was canceled", "AbortError");

const settle = (waiter: Waiter, outcome: () => void) => {
  if (waiter.settled) return;
  waiter.settled = true;
  outcome();
};

/** A placement nobody waits for any more has gone: its answer would reach no card. */
const gone = (placement: Placement) =>
  placement.waiters.every((waiter) => waiter.settled);

/**
 * Batches one surface keeps unfinished at once (#12902). A batch no longer holds a web worker:
 * it is a render job the page reads until it is done. The server lets each user have two
 * unfinished jobs (`RMS_RENDER_BATCH_USER_MAX_INFLIGHT`) and turns a third away as busy, so a
 * surface that opens many cards at once keeps within that cap rather than being refused.
 */
export const MAX_REQUESTS_IN_FLIGHT = 2;

/** The longest a server's `Retry-After` is waited for at a time. */
export const BUSY_RETRY_MAX_WAIT_MS = 5000;
/** At most this much is added to each wait, so the pages a busy server turned away spread out. */
export const BUSY_JITTER_MS = 250;
/** The shortest wait, whatever the server asks: ten sends to a server saying "now" never burst. */
export const BUSY_MIN_WAIT_MS = 250;
/**
 * How long a batch may wait in all for a busy server before its cards fail (and offer Retry):
 * about what a reader waits on loading cards before deciding something is wrong.
 */
export const BUSY_RETRY_DEADLINE_MS = 15_000;
/** The most times a batch is sent to a busy server, however short the waits it asks for. */
export const BUSY_RETRY_ATTEMPTS = 10;

export interface PageRenderBatcherOptions {
  /**
   * Requests kept open at once (`MAX_REQUESTS_IN_FLIGHT` unless a surface asks for fewer). A
   * function is read each time a request could go, for a surface whose allowance changes (a
   * dashboard keeps two while its first tab paints, one after).
   */
  maxInFlight?: number | (() => number);
  /** The jitter's random source, 0 to 1. */
  random?: () => number;
  /**
   * Ask the server's render cache on every request (the dashboard surfaces; rms-dashboards,
   * Rendering and speed). Absent, every answer is a live render.
   */
  cache?: boolean;
  /**
   * The page's user-room pushes (#12902): a batch waiting on its render job reads it as soon
   * as a push for it lands. Absent, a batch reads its job by polling alone.
   */
  push?: RenderPushSource;
  /** What the surface calls its components, for the failures it raises; a card when absent. */
  noun?: ComponentNoun;
}

/** How many of the latest pushed batch ids a page's push source remembers. */
export const RENDER_PUSH_MEMORY = 16;

/** A page's push source: the page's user-room listener notifies it, its batches subscribe. */
export interface RenderPushChannel extends RenderPushSource {
  /** A push for `batchId` landed: wake its batch, or the batch that subscribes for it next. */
  notify(batchId: string): void;
}

/**
 * The push source one page creates for its batches (#12902): its user-room listener
 * (`useRenderPush`) notifies it of each `RMS_RENDER_BATCH_PROGRESS` frame, and each batch
 * waiting on its render job subscribes by its id. A push can land before its batch subscribes
 * (the POST's answer still on its way), so the last `RENDER_PUSH_MEMORY` pushed ids are kept
 * and a batch subscribing for one of them is woken at once. A push nobody waits on wakes
 * nothing. It lives as long as the page that created it.
 */
export function createRenderPushSource(): RenderPushChannel {
  const waiting = new Map<string, Set<() => void>>();
  const recent: string[] = [];
  return {
    notify(batchId) {
      const known = recent.indexOf(batchId);
      if (known >= 0) recent.splice(known, 1);
      recent.push(batchId);
      if (recent.length > RENDER_PUSH_MEMORY) recent.shift();
      for (const wake of [...(waiting.get(batchId) ?? [])]) wake();
    },
    subscribe(batchId, wake) {
      const wakes = waiting.get(batchId) ?? new Set<() => void>();
      waiting.set(batchId, wakes);
      wakes.add(wake);
      if (recent.includes(batchId)) wake();
      return () => {
        wakes.delete(wake);
        if (!wakes.size && waiting.get(batchId) === wakes)
          waiting.delete(batchId);
      };
    },
  };
}

/**
 * One page's renders, sent as streamed batches (#11849 render latency).
 *
 * Every frame that asks for its document in the same task joins the requests for its page
 * context, `batchSize` placements each, so a page's cards share a few round trips and their
 * authorization overhead, and each card paints as its own line arrives. Every surface passes
 * `RENDER_ROUND_PLACEMENTS`, one round of renders on a worker's warm brokers; the default, the
 * server's per-request bound, is only the most a request may carry. A failed line fails only its card;
 * Retry and table paging ask again for that card alone. At most `MAX_REQUESTS_IN_FLIGHT`
 * requests are open at once; the rest wait, in order, until one ends.
 *
 * Two frames asking for the same card (the same context, component, revision and table
 * requests) while it is waiting or in flight share one placement and one live render, each
 * keeping its own abort. That is one request answered twice, not a cache: nothing outlives
 * the request, and an ask after it has answered renders again. A surface created with `cache`
 * asks the server's render cache on every request; an ask with `fresh` goes in a request of its
 * own that renders past it.
 */
export function createPageRenderBatcher(
  batchSize: number = MAX_BATCH_PLACEMENTS,
  {
    maxInFlight = MAX_REQUESTS_IN_FLIGHT,
    random = Math.random,
    cache = false,
    push,
    noun = "card",
  }: PageRenderBatcherOptions = {}
): PageRenderBatcher {
  const waiting = new Map<string, Group>();
  const queued: Group[] = [];
  // Placements not yet answered, waiting or in flight, by context and placement key.
  const open = new Map<string, Placement>();
  let inFlight = 0;
  let flushQueued = false;

  const close = (contextKey: string, placement: Placement) => {
    placement.done = true;
    const openKey = `${contextKey}\u0000${placement.key}`;
    if (open.get(openKey) === placement) open.delete(openKey);
  };

  const dropAborted = (contextKey: string, placements: Placement[]) => {
    for (const placement of placements) {
      for (const waiter of placement.waiters)
        if (waiter.signal.aborted)
          settle(waiter, () => waiter.reject(abortError()));
      if (gone(placement)) close(contextKey, placement);
    }
    return placements.filter((placement) => !gone(placement));
  };

  const allowance = (): number =>
    typeof maxInFlight === "function" ? maxInFlight() : maxInFlight;
  const pump = () => {
    while (inFlight < allowance() && queued.length) {
      const next = queued.shift();
      if (!next) return;
      const contextKey = groupKey(next.context, next.fresh);
      const live = dropAborted(contextKey, next.placements);
      if (live.length) send(next.context, contextKey, live, next.fresh);
    }
  };

  const send = (
    context: PageSelector,
    contextKey: string,
    live: Placement[],
    fresh: boolean
  ) => {
    const controller = new AbortController();
    inFlight += 1;
    let ended = false;
    // A request's place frees once, however it ends: answered, broken or abandoned.
    const end = () => {
      if (ended) return;
      ended = true;
      inFlight -= 1;
      pump();
    };
    controller.signal.addEventListener("abort", end, { once: true });
    // The askers' abort listeners last as long as the request does, and no longer.
    const listening = new AbortController();
    const abandon = () => {
      // The request stops once no asker is waiting on it: every one has either its answer
      // or gone away.
      if (live.every(gone)) controller.abort();
    };
    const watch = (placement: Placement, waiter: Waiter) =>
      waiter.signal.addEventListener(
        "abort",
        () => {
          settle(waiter, () => waiter.reject(abortError()));
          if (gone(placement)) close(contextKey, placement);
          abandon();
        },
        { once: true, signal: listening.signal }
      );
    for (const placement of live) {
      for (const waiter of placement.waiters) watch(placement, waiter);
      placement.join = (waiter) => {
        placement.waiters.push(waiter);
        watch(placement, waiter);
      };
    }
    const answer = (line: BatchLine) => {
      const placement = live[line.index];
      if (!placement || placement.done) return;
      close(contextKey, placement);
      // Every card answered: an asker leaving from here on has nothing left to abandon. Not a
      // duplicate of `finish`'s: the stream's end reaches `finish` only some microtasks after
      // the last line (the busy-retry loop awaits it), and an asker that leaves in between
      // must not abort a request that has already answered everyone.
      if (live.every((each) => each.done)) listening.abort();
      for (const waiter of placement.waiters)
        settle(waiter, () =>
          line.status === "ok"
            ? waiter.resolve({
                document: line.document,
                cachedAt: line.cachedAt,
                kindsRead: line.kindsRead,
                kindRecorded: line.kindRecorded,
                ...(line.stale ? { stale: true } : {}),
              })
            : waiter.reject(new RenderLineError(line.message, line.code))
        );
    };
    const finish = (error: Error) => {
      listening.abort();
      for (const placement of live) {
        close(contextKey, placement);
        for (const waiter of placement.waiters)
          settle(waiter, () => waiter.reject(error));
      }
      end();
    };
    const flags: RenderBatchOptions = {
      ...(cache ? { cache: true } : {}),
      ...(fresh ? { fresh: true } : {}),
      ...(push ? { push } : {}),
      ...(noun === "card" ? {} : { noun }),
    };
    const placements = live.map((placement) => ({
      component_id: placement.component.id,
      revision_id: placement.component.current_revision_id,
      tables: placement.tables,
    }));
    // A busy server (its admission cap) turned the batch away before rendering any of it: wait
    // out its Retry-After and send the same batch again, holding this request's place, until
    // the deadline; the cards stay loading meanwhile.
    const stream = async () => {
      let waited = 0;
      for (let attempt = 1; ; attempt += 1) {
        try {
          await streamRenderBatch(
            context,
            placements,
            controller.signal,
            answer,
            flags
          );
          return;
        } catch (error: unknown) {
          if (!(error instanceof RenderBusyError) || controller.signal.aborted)
            throw error;
          // A floor under the wait asked for, so a server saying "now" is not sent ten
          // requests in a burst, and a jitter of at most that floor.
          const asked = Math.min(
            Math.max(error.retryAfterMs, BUSY_MIN_WAIT_MS),
            BUSY_RETRY_MAX_WAIT_MS
          );
          const wait = asked + random() * BUSY_JITTER_MS;
          if (
            attempt >= BUSY_RETRY_ATTEMPTS ||
            waited + wait > BUSY_RETRY_DEADLINE_MS
          )
            throw new Error("The server is busy. Please retry.");
          waited += wait;
          await productDelay(wait);
          if (controller.signal.aborted) throw abortError();
        }
      }
    };
    stream().then(
      // A line the server never sent leaves its card failed, not loading forever.
      () => finish(new Error(didNotAnswer(noun))),
      (error: unknown) => {
        // A broken stream is abandoned, so the server stops rendering for it.
        controller.abort();
        finish(error instanceof Error ? error : new Error(failedToLoad(noun)));
      }
    );
  };

  const flush = () => {
    flushQueued = false;
    const groups = [...waiting.values()];
    waiting.clear();
    for (const { context, fresh, placements } of groups)
      for (let start = 0; start < placements.length; start += batchSize)
        queued.push({
          context,
          fresh,
          placements: placements.slice(start, start + batchSize),
        });
    pump();
  };

  const kept: RenderKept = (component, context, tables, signal, ask = {}) =>
    new Promise<KeptAnswer>((resolve, reject) => {
      if (signal.aborted) {
        reject(abortError());
        return;
      }
      const fresh = ask.fresh === true;
      const waiter: Waiter = { signal, resolve, reject, settled: false };
      const contextKey = groupKey(context, fresh);
      const key = JSON.stringify([
        component.id,
        component.current_revision_id,
        tables,
      ]);
      const openKey = `${contextKey}\u0000${key}`;
      const shared = open.get(openKey);
      if (shared && !shared.done) {
        // Already waiting or in flight: this asker shares its answer.
        if (shared.join) shared.join(waiter);
        else shared.waiters.push(waiter);
        return;
      }
      const placement: Placement = {
        key,
        component,
        tables,
        waiters: [waiter],
        join: null,
        done: false,
      };
      open.set(openKey, placement);
      const group = waiting.get(contextKey) ?? {
        context,
        fresh,
        placements: [],
      };
      waiting.set(contextKey, group);
      group.placements.push(placement);
      // Frames ask for their documents from the same commit, one microtask each; the
      // request goes out once they all have, in the microtask after the last.
      if (!flushQueued) {
        flushQueued = true;
        queueMicrotask(flush);
      }
    });
  const render: RenderPlacement = (component, context, tables, signal) =>
    kept(component, context, tables, signal).then(({ document }) => document);
  return Object.assign(render, { kept });
}

/** Asks for one page context share requests, and a fresh ask never joins a cached one. */
const groupKey = (context: PageSelector, fresh: boolean) =>
  JSON.stringify([context, fresh]);
